import { useState } from "react";
import { Twitter, Linkedin, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

const SITE_URL = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "");

/**
 * Post-signup share card for /courtroom.
 *
 * After someone claims a founding spot we surface a one-click "tell other
 * lawyers" CTA. Each share is free distribution back to /courtroom — every
 * signup compounds into more visibility.
 *
 * Spot number comes from the waitlist endpoint so the share copy is
 * personal ("I just claimed founding spot #12 of 100").
 */
export default function FoundingShareCard({ spotNumber, foundingTotal = 100 }) {
    const [copied, setCopied] = useState(false);
    const baseUrl = `${SITE_URL}/courtroom`;
    const safeSpot = spotNumber && spotNumber > 0 ? spotNumber : null;
    // Append ?ref=<spot> so every share is a tracked referral link.
    const shareUrl = safeSpot ? `${baseUrl}?ref=${safeSpot}` : baseUrl;

    const tweetText = safeSpot
        ? `I just claimed founding member spot #${safeSpot} of ${foundingTotal} on HPM Courtroom — finally a place lawyers can practice trying real cases against 10 adversarial AI bots (judge, opposing counsel, witnesses, jury). $99 lifetime, ${foundingTotal - safeSpot} spots left. Use my link — we both get priority access on launch:`
        : `Just locked my founding member spot on HPM Courtroom — mock-trial practice against 10 adversarial AI bots (judge, opposing counsel, jury). $99 lifetime founding deal.`;

    const liText = safeSpot
        ? `Founding member #${safeSpot} of ${foundingTotal} on HPM Courtroom. Mock-trial training against 10 adversarial AI bots — the practice every young trial lawyer wishes existed. Use my link to claim a spot and we both get priority access on launch.`
        : `Locked in on HPM Courtroom — AI mock-trial training for lawyers. The practice every young trial lawyer wishes existed.`;

    const tweetHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`;
    const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(liText)}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied — paste it anywhere");
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("Couldn't copy. Long-press the link instead.");
        }
    };

    return (
        <div
            className="mt-5 border-2 border-[#00FF85]/40 bg-[#00FF85]/5 p-5"
            data-testid="founding-share-card"
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85] mb-2">
                {safeSpot ? `FOUNDING MEMBER #${safeSpot} OF ${foundingTotal}` : "FOUNDING MEMBER · CONFIRMED"}
            </div>
            <div className="font-anton text-xl uppercase leading-none mb-2">
                One favor.
            </div>
            <p className="font-sans text-sm text-white/75 leading-relaxed mb-4">
                Tell one other lawyer. Every founding member who shares this
                page directly funds the build — and gets you closer to launch.
            </p>
            <div className="flex flex-wrap gap-2">
                <a
                    href={tweetHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white/60 transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white"
                    data-testid="share-card-twitter"
                >
                    <Twitter size={12} /> POST ON X
                </a>
                <a
                    href={liHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white/60 transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white"
                    data-testid="share-card-linkedin"
                >
                    <Linkedin size={12} /> SHARE ON LINKEDIN
                </a>
                <button
                    onClick={copyLink}
                    className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white/60 transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white"
                    data-testid="share-card-copy-link"
                >
                    {copied ? <Check size={12} /> : <LinkIcon size={12} />}
                    {copied ? "COPIED" : "COPY LINK"}
                </button>
            </div>
        </div>
    );
}
